<template>
  <el-drawer
    title="Agent 辅助申请"
    :visible.sync="drawerVisible"
    direction="rtl"
    size="520px"
    custom-class="apply-assist-drawer"
    append-to-body
    :wrapper-closable="false"
  >
    <div class="apply-assist-summary">
      <div class="apply-assist-summary-title" @click="summaryExpanded = !summaryExpanded">
        <span>已识别信息</span>
        <i :class="summaryExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
      </div>
      <div v-show="summaryExpanded" class="apply-assist-summary-body">
        <div class="apply-assist-summary-row"><span>任务名称</span><b>{{ draft.taskName || '待确认' }}</b></div>
        <div class="apply-assist-summary-row"><span>渠道</span><b>{{ formatDraftChannels(draft) || '待选择' }}</b></div>
        <div class="apply-assist-summary-row"><span>采集模式</span><b>{{ draft.collectMode || '待确认' }}</b></div>
        <div class="apply-assist-summary-row"><span>采集周期</span><b>{{ formatDraftPeriod(draft) || '待确认' }}</b></div>
        <div class="apply-assist-summary-row"><span>监控对象</span><b>{{ draft.monitorObject || '待确认' }}</b></div>
        <div class="apply-assist-summary-row"><span>关键词</span><b>{{ formatDraftKeywords(draft) || '待选择方案' }}</b></div>
        <div v-if="missingLabels.length" class="apply-assist-missing">
          还缺：{{ missingLabels.join('、') }}
        </div>
      </div>
    </div>

    <div ref="assistBody" class="apply-assist-body">
      <div
        v-for="item in conversationItems"
        :key="'item-' + item.id"
        class="apply-assist-item"
        :class="'is-' + item.type"
      >
        <div
          v-if="item.type === 'message'"
          class="apply-assist-message"
          :class="'is-' + item.role"
        >
          {{ item.text }}
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'channels'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">选择渠道</div>
          <el-cascader
            v-model="actionUi.channelPaths"
            :options="channelOptions"
            :props="channelCascaderProps"
            collapse-tags
            clearable
            filterable
            :disabled="busy"
            placeholder="请选择渠道（可多选）"
            style="width: 100%"
            @change="onChannelsChange"
          />
          <div class="apply-assist-card-actions">
            <el-button
              type="primary"
              size="small"
              :disabled="busy || !actionUi.channelPaths.length"
              @click="confirmChannels"
            >
              确认渠道
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'keywords'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">关键词方案</div>
          <el-input
            v-model="actionUi.keywords.object"
            placeholder="监控对象，如：朱梓骁"
            maxlength="50"
            clearable
            :disabled="busy"
            @keyup.enter.native="fetchKeywordRecommend"
          />
          <el-input
            v-model="actionUi.keywords.hint"
            placeholder="对象说明（选填），如：快手主播"
            maxlength="100"
            clearable
            :disabled="busy"
            style="margin-top: 8px"
            @keyup.enter.native="fetchKeywordRecommend"
          />
          <el-button
            type="primary"
            size="small"
            style="margin-top: 8px"
            :loading="keywordCardLoading"
            :disabled="assistLoading || actionUi.keywords.loading"
            @click="fetchKeywordRecommend"
          >
            {{ actionUi.keywords.schemes.length ? '重新生成' : '生成推荐方案' }}
          </el-button>
          <div v-if="actionUi.keywords.error" class="apply-assist-card-error">
            {{ actionUi.keywords.error }}
            <el-button type="text" :disabled="busy" @click="fetchKeywordRecommend">重试</el-button>
          </div>
          <keyword-recommend-schemes
            v-if="actionUi.keywords.schemes.length"
            style="margin-top: 10px"
            :schemes="actionUi.keywords.schemes"
            :selected="actionUi.keywords.selected"
            @update:selected="onSelectScheme"
          />
          <div class="apply-assist-card-actions">
            <el-button
              type="primary"
              size="small"
              :disabled="busy || actionUi.keywords.selected < 0"
              @click="confirmKeywordScheme"
            >
              确认所选方案
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'collectMode'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">选择采集模式</div>
          <div class="apply-assist-mode-actions">
            <el-button
              size="small"
              :disabled="busy"
              @click="confirmCollectMode(collectModeHistory)"
            >
              {{ collectModeHistory }}
            </el-button>
            <el-button
              size="small"
              :disabled="busy"
              @click="confirmCollectMode(collectModeRealtime)"
            >
              {{ collectModeRealtime }}
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'collectPeriod'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">选择采集周期</div>
          <el-date-picker
            :key="'period-' + item.id"
            v-model="actionUi.periodRange"
            type="datetimerange"
            range-separator="→"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="periodPickerOptions"
            :disabled="busy"
            style="width: 100%"
          />
          <div class="apply-assist-card-actions">
            <el-button
              type="primary"
              size="small"
              :disabled="busy || !actionUi.periodRange || actionUi.periodRange.length !== 2"
              @click="confirmCollectPeriod"
            >
              确认周期
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'taskName'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">确认任务名称</div>
          <el-input
            v-model="actionUi.taskName"
            placeholder="请输入任务名称"
            clearable
            :disabled="busy"
            @keyup.enter.native="confirmTaskName"
          />
          <div class="apply-assist-card-actions">
            <el-button
              type="primary"
              size="small"
              :disabled="busy || !(actionUi.taskName || '').trim()"
              @click="confirmTaskName"
            >
              确认名称
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'estimate'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">预估采集量级</div>
          <div v-if="estimateLoading" class="apply-assist-estimate-loading">
            <i class="el-icon-loading"></i>
            <span>正在按以往数据预估量级…</span>
          </div>
          <template v-else-if="estimateFailed">
            <div class="apply-assist-card-error">{{ estimateFailureText }}</div>
            <div class="apply-assist-card-actions">
              <el-button size="small" @click="retryEstimate">重试预估</el-button>
              <el-button type="primary" size="small" @click="skipEstimate">跳过，先生成草稿</el-button>
            </div>
          </template>
          <template v-else-if="hasEstimateCount">
            <div class="apply-assist-estimate-text">
              按当前渠道、关键词和采集周期，预估约 {{ estimatedCount }} 条。
            </div>
            <div class="apply-assist-estimate-disclaimer">
              这是预估，根据以往数据推算，仅供参考，不是实际采集量。
            </div>
            <div v-if="risk && risk.level === 'high'" class="apply-assist-estimate-disclaimer">
              当前配置采集面偏宽，预估可能偏高。
            </div>
            <div class="apply-assist-card-actions">
              <el-button size="small" @click="rejectEstimateTooLarge">量太大，去调整</el-button>
              <el-button type="primary" size="small" @click="acceptEstimate">可以接受</el-button>
            </div>
          </template>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'riskHint' && item.scope === 'channels'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">渠道范围提示</div>
          <div class="apply-assist-estimate-text">{{ channelRiskText }}</div>
          <div class="apply-assist-card-actions">
            <el-button size="small" :disabled="busy" @click="editFromRiskHint('channels')">调整渠道</el-button>
            <el-button type="primary" size="small" :disabled="busy" @click="continueAfterRiskHint">
              继续选关键词
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'riskHint' && item.scope === 'keywords'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">配置风险提示</div>
          <div
            v-for="(fact, index) in keywordRiskFacts"
            :key="'risk-fact-' + index"
            class="apply-assist-risk-fact"
          >
            {{ fact }}
          </div>
          <div class="apply-assist-estimate-disclaimer">
            建议增加对象/品牌核心词作为一组，场景词（投诉、负面等）放另一组，组间为且。
          </div>
          <div class="apply-assist-card-actions">
            <el-button size="small" :disabled="busy" @click="editFromRiskHint('channels')">调整渠道</el-button>
            <el-button size="small" :disabled="busy" @click="editFromRiskHint('keywords')">优化关键词</el-button>
            <el-button type="primary" size="small" :disabled="busy" @click="continueAfterRiskHint">
              仍按当前配置继续
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'action' && item.status === 'active' && item.kind === 'review'"
          class="apply-assist-card"
        >
          <div class="apply-assist-card-title">必填信息已齐，可以生成申请草稿</div>
          <div class="apply-assist-card-actions">
            <el-button size="small" :disabled="busy" @click="dismissReviewCard">继续修改</el-button>
            <el-button type="primary" size="small" :disabled="!canGenerateDraft || busy" @click="adoptDraft">
              生成申请草稿
            </el-button>
          </div>
        </div>

        <div
          v-else-if="item.type === 'receipt'"
          class="apply-assist-receipt"
        >
          <div class="apply-assist-receipt-head">
            <span>{{ receiptTitle(item.kind) }}</span>
            <el-button type="text" :disabled="assistLoading || evaluateLoading" @click="editReceipt(item)">修改</el-button>
          </div>
          <div class="apply-assist-receipt-body">{{ item.snapshotText }}</div>
        </div>
      </div>
    </div>

    <div class="apply-assist-composer">
      <el-input
        v-model="inputText"
        type="textarea"
        :rows="3"
        maxlength="2000"
        :disabled="busy"
        :placeholder="composerPlaceholder"
      />
      <el-button
        type="primary"
        :loading="assistLoading"
        :disabled="busy"
        @click="sendMessage"
      >发送</el-button>
    </div>
    <div class="apply-assist-footer">
      <el-button @click="drawerVisible = false">取消</el-button>
      <el-button type="primary" :disabled="!canGenerateDraft || busy" @click="adoptDraft">
        生成申请草稿
      </el-button>
    </div>
  </el-drawer>
</template>

<script>
import { assistTaskApply, estimateApply, evaluateTaskApply, recommendKeywordSchemes } from './api'
import {
  CHANNEL_CASCADER_PROPS,
  CHANNEL_OPTIONS,
  entriesToChannelPaths
} from './channelOptions'
import KeywordRecommendSchemes from './KeywordRecommendSchemes.vue'
import {
  MISSING_FIELD_LABELS,
  buildEstimatePayloadFromDraft,
  channelsFromPaths,
  computeMissingFields,
  emptyApplyDraft,
  formatDraftChannels,
  formatDraftKeywords,
  formatDraftPeriod,
  normalizeApplyDraft
} from './taskApplyDraft'
import {
  COLLECT_MODE_HISTORY,
  COLLECT_MODE_REALTIME,
  buildCollectModePickerOptions,
  buildDateTimeRange,
  formatEstimateText,
  toApiDateTime,
  validateCollectDateRange
} from './taskFormUtils'

const WELCOME_TEXT = '请用一句话描述要监测的对象、渠道和采集周期。渠道和关键词在对话里的卡片中选择。'

const NEXT_ACTION_CHANNELS = 'channels'
const NEXT_ACTION_KEYWORDS = 'keywords'
const NEXT_ACTION_COLLECT_MODE = 'collectMode'
const NEXT_ACTION_COLLECT_PERIOD = 'collectPeriod'
const NEXT_ACTION_TASK_NAME = 'taskName'
const NEXT_ACTION_REVIEW = 'review'
const ACTION_KIND_ESTIMATE = 'estimate'
const ACTION_KIND_RISK_HINT = 'riskHint'
const RISK_SCOPE_CHANNELS = 'channels'
const RISK_SCOPE_KEYWORDS = 'keywords'
const RISK_LEVEL_WARNING = 'warning'
const RISK_LEVEL_HIGH = 'high'
const RISK_ISSUE_CHANNEL_SCOPE_WIDE = 'CHANNEL_SCOPE_WIDE'
const RISK_ISSUE_MISSING_OBJECT_ANCHOR = 'MISSING_OBJECT_ANCHOR'
const RISK_ISSUE_BROAD_SINGLE_OR_GROUP = 'BROAD_SINGLE_OR_GROUP'

const RULE_CONFIRM_KINDS = [
  NEXT_ACTION_CHANNELS,
  NEXT_ACTION_KEYWORDS,
  NEXT_ACTION_COLLECT_MODE,
  NEXT_ACTION_COLLECT_PERIOD
]

const COMPOSER_PLACEHOLDERS = {
  channels: '也可以在上方卡片里选择渠道',
  keywords: '在卡片里填写监控对象并选择方案',
  collectMode: '请在卡片中选择采集模式',
  collectPeriod: '请在卡片中选择采集周期',
  taskName: '请在卡片中确认任务名称',
  review: '还可以补充说明，或直接生成申请草稿'
}

const RECEIPT_TITLES = {
  channels: '已确认渠道',
  keywords: '已确认关键词方案',
  collectMode: '已确认采集模式',
  collectPeriod: '已确认采集周期',
  taskName: '已确认任务名称'
}

function emptyActionUi() {
  return {
    channelPaths: [],
    keywords: {
      object: '',
      hint: '',
      schemes: [],
      selected: -1,
      error: '',
      loading: false,
      requestActionId: 0
    },
    periodRange: [],
    periodAnchor: null,
    taskName: ''
  }
}

export default {
  name: 'TaskApplyAgentDrawer',
  components: { KeywordRecommendSchemes },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      channelOptions: CHANNEL_OPTIONS,
      channelCascaderProps: CHANNEL_CASCADER_PROPS,
      conversationItems: [],
      itemSeq: 0,
      collectModeHistory: COLLECT_MODE_HISTORY,
      collectModeRealtime: COLLECT_MODE_REALTIME,
      draft: emptyApplyDraft(),
      missingFields: computeMissingFields(emptyApplyDraft()),
      ready: false,
      nextAction: NEXT_ACTION_CHANNELS,
      actionUi: emptyActionUi(),
      summaryExpanded: true,
      inputText: '',
      assistLoading: false,
      evaluateLoading: false,
      sessionSeq: 0,
      estimateAccepted: false,
      estimateSkipped: false,
      estimatedCount: null,
      estimatedAt: null,
      estimateError: '',
      estimateLoading: false,
      estimateSeq: 0,
      estimateRuleKey: '',
      risk: null,
      riskHintAckFingerprint: ''
    }
  },
  watch: {
    visible(value, previous) {
      // 只在关闭→打开时重置；打开期间 visible 保持 true 不得清会话
      if (value && !previous) {
        this.resetSession()
      } else if (!value) {
        this.invalidateSession()
      }
    }
  },
  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      }
    },
    busy() {
      return this.assistLoading || this.evaluateLoading || this.actionUi.keywords.loading || this.estimateLoading
    },
    canGenerateDraft() {
      return this.ready && (this.estimateAccepted || this.estimateSkipped)
    },
    estimateFailed() {
      return !this.estimateLoading && !!this.estimateError
    },
    hasEstimateCount() {
      return !this.estimateLoading && !this.estimateError && this.estimatedCount != null
    },
    estimateFailureText() {
      return formatEstimateText(null, this.estimatedAt, this.estimateError) || this.estimateError
    },
    composerPlaceholder() {
      return COMPOSER_PLACEHOLDERS[this.nextAction] || ''
    },
    periodPickerOptions() {
      const options = buildCollectModePickerOptions(this.draft.collectMode, () => this.actionUi.periodAnchor)
      options.onPick = ({ minDate, maxDate }) => {
        this.actionUi.periodAnchor = maxDate ? null : (minDate || null)
      }
      return options
    },
    missingLabels() {
      return (this.missingFields || [])
        .map(key => MISSING_FIELD_LABELS[key] || key)
    },
    // 推荐 loading 只挂在发起请求的那张卡上，避免「修改」换卡后误显示转圈
    keywordCardLoading() {
      const active = this.findActiveAction()
      return !!(
        this.actionUi.keywords.loading &&
        active &&
        active.kind === 'keywords' &&
        active.id === this.actionUi.keywords.requestActionId
      )
    },
    channelRiskText() {
      const count = this.risk && this.risk.channelCount != null ? this.risk.channelCount : 0
      return '当前已选择 ' + count + ' 个渠道，范围较大。如果后续关键词较宽泛，采集量可能明显增加。'
    },
    keywordRiskFacts() {
      const issues = this.risk && Array.isArray(this.risk.issues) ? this.risk.issues : []
      const count = this.risk && this.risk.channelCount != null ? this.risk.channelCount : 0
      const facts = []
      if (issues.indexOf(RISK_ISSUE_CHANNEL_SCOPE_WIDE) >= 0) {
        facts.push('当前已选择 ' + count + ' 个渠道，范围较大。')
      }
      if (issues.indexOf(RISK_ISSUE_MISSING_OBJECT_ANCHOR) >= 0) {
        facts.push('未发现包含监控对象的核心词组。')
      }
      if (issues.indexOf(RISK_ISSUE_BROAD_SINGLE_OR_GROUP) >= 0) {
        facts.push('当前只有一组通用词。')
      }
      return facts
    }
  },
  methods: {
    formatDraftChannels,
    formatDraftPeriod,
    formatDraftKeywords,
    receiptTitle(kind) {
      return RECEIPT_TITLES[kind] || '已确认'
    },
    nextItemId() {
      this.itemSeq += 1
      return this.itemSeq
    },
    findActiveAction() {
      return this.conversationItems.find(item => item.type === 'action' && item.status === 'active') || null
    },
    isKeywordActionCurrent(actionId) {
      const active = this.findActiveAction()
      return !!(active && active.kind === 'keywords' && active.id === actionId)
    },
    invalidateSession() {
      this.sessionSeq += 1
      this.assistLoading = false
      this.evaluateLoading = false
      this.actionUi.keywords.loading = false
      this.invalidateEstimateRequest()
    },
    resetEstimateSession() {
      this.estimateAccepted = false
      this.estimateSkipped = false
      this.estimatedCount = null
      this.estimatedAt = null
      this.estimateError = ''
      this.estimateLoading = false
      this.estimateSeq = 0
      this.estimateRuleKey = ''
    },
    // 渠道/关键词/模式/周期变更后作废旧预估；不写入 draft
    clearEstimateDecisionAndResult() {
      this.estimateAccepted = false
      this.estimateSkipped = false
      this.estimatedCount = null
      this.estimatedAt = null
      this.estimateError = ''
      this.invalidateEstimateRequest()
    },
    invalidateEstimateRequest() {
      this.estimateSeq += 1
      this.estimateLoading = false
    },
    computeEstimateRuleKey() {
      const current = normalizeApplyDraft(this.draft)
      const channels = (current.channels || [])
        .map(item => (item && item.pubCode) || '')
        .filter(Boolean)
        .slice()
        .sort()
      const groups = current.keywordGroups.map(group => (group.words || []).slice().sort())
      return JSON.stringify({
        channels: channels,
        collectMode: current.collectMode,
        startTime: current.startTime,
        endTime: current.endTime,
        keywordGroups: groups,
        excludeWords: current.excludeWords.slice().sort()
      })
    },
    invalidateEstimateIfRuleChanged() {
      const key = this.computeEstimateRuleKey()
      if (this.estimateRuleKey === key) return false
      this.clearEstimateDecisionAndResult()
      this.estimateRuleKey = key
      return true
    },
    resetSession() {
      this.invalidateSession()
      this.draft = emptyApplyDraft()
      this.missingFields = computeMissingFields(this.draft)
      this.ready = false
      this.nextAction = NEXT_ACTION_CHANNELS
      this.evaluateLoading = false
      this.actionUi = emptyActionUi()
      this.inputText = ''
      this.summaryExpanded = true
      this.itemSeq = 0
      this.conversationItems = []
      this.resetEstimateSession()
      this.risk = null
      this.riskHintAckFingerprint = ''
      this.pushMessage('assistant', WELCOME_TEXT)
      this.ensureActionCards()
      this.scrollBodyToLatest()
    },
    pushMessage(role, text) {
      this.conversationItems.push({
        id: this.nextItemId(),
        type: 'message',
        role: role,
        text: text
      })
    },
    pushReceipt(kind, snapshotText) {
      this.conversationItems.push({
        id: this.nextItemId(),
        type: 'receipt',
        kind: kind,
        snapshotText: snapshotText
      })
    },
    appendAction(kind, fromReceiptEdit) {
      this.conversationItems.push({
        id: this.nextItemId(),
        type: 'action',
        kind: kind,
        status: 'active',
        fromReceiptEdit: !!fromReceiptEdit
      })
    },
    dismissActiveAction() {
      const active = this.findActiveAction()
      if (!active) return
      // 不写 draft；进行中的推荐按 sessionSeq 与发起时的 action.id 决定是否采纳
      this.conversationItems = this.conversationItems.filter(item => item.id !== active.id)
    },
    prefillChannelActionUi() {
      this.actionUi.channelPaths = entriesToChannelPaths(this.draft.channels)
    },
    prefillKeywordActionUi() {
      // 新卡只从 draft 预填；不沿用已关掉那张卡的方案/选中，避免半成品污染
      this.actionUi.keywords.object = this.draft.monitorObject || ''
      this.actionUi.keywords.hint = this.draft.monitorHint || ''
      this.actionUi.keywords.selected = -1
      this.actionUi.keywords.error = ''
      this.actionUi.keywords.schemes = []
    },
    prefillPeriodActionUi() {
      this.actionUi.periodRange = buildDateTimeRange(this.draft.startTime, this.draft.endTime)
      this.actionUi.periodAnchor = null
    },
    prefillTaskNameActionUi() {
      this.actionUi.taskName = this.draft.taskName || ''
    },
    findReviewAction() {
      return this.conversationItems.find(item => item.type === 'action' && item.kind === NEXT_ACTION_REVIEW) || null
    },
    removeReviewActions() {
      this.conversationItems = this.conversationItems.filter(
        item => !(item.type === 'action' && item.kind === NEXT_ACTION_REVIEW)
      )
    },
    applyServerSnapshot(result) {
      this.draft = normalizeApplyDraft(result && result.draft)
      this.missingFields = Array.isArray(result && result.missingFields)
        ? result.missingFields.slice()
        : []
      this.ready = result && result.ready === true
      this.nextAction = (result && result.nextAction) || ''
      this.risk = result && result.risk ? result.risk : null
    },
    computeRiskHintFingerprint() {
      const current = normalizeApplyDraft(this.draft)
      const channels = (current.channels || [])
        .map(item => (item && item.pubCode) || '')
        .filter(Boolean)
        .slice()
        .sort()
      const groups = current.keywordGroups.map(group => (group.words || []).slice().sort())
      return JSON.stringify({
        channels: channels,
        keywordGroups: groups,
        keywordsConfirmed: current.keywordsConfirmed === true,
        monitorObject: current.monitorObject || ''
      })
    },
    shouldInsertRiskHint(kind) {
      if (!this.risk) return false
      const fingerprint = this.computeRiskHintFingerprint()
      if (fingerprint && this.riskHintAckFingerprint === fingerprint) return false
      const issues = Array.isArray(this.risk.issues) ? this.risk.issues : []
      if (kind === NEXT_ACTION_CHANNELS) {
        return issues.indexOf(RISK_ISSUE_CHANNEL_SCOPE_WIDE) >= 0
      }
      if (kind === NEXT_ACTION_KEYWORDS) {
        return this.risk.level === RISK_LEVEL_WARNING || this.risk.level === RISK_LEVEL_HIGH
      }
      return false
    },
    appendRiskHint(scope) {
      this.dismissActiveAction()
      this.conversationItems.push({
        id: this.nextItemId(),
        type: 'action',
        kind: ACTION_KIND_RISK_HINT,
        scope: scope,
        status: 'active'
      })
    },
    continueAfterRiskHint() {
      if (this.busy) return
      this.riskHintAckFingerprint = this.computeRiskHintFingerprint()
      this.dismissActiveAction()
      this.ensureActionCards()
      this.scrollBodyToLatest()
    },
    // 不记已看过；末尾重开对应卡，与回执「修改」相同
    editFromRiskHint(kind) {
      if (this.busy) return
      this.dismissActiveAction()
      if (kind === NEXT_ACTION_CHANNELS) {
        this.prefillChannelActionUi()
        this.appendAction(NEXT_ACTION_CHANNELS, true)
      } else if (kind === NEXT_ACTION_KEYWORDS) {
        this.prefillKeywordActionUi()
        this.appendAction(NEXT_ACTION_KEYWORDS, true)
      }
      this.scrollBodyToLatest()
    },
    async refreshFromEvaluate() {
      const result = await evaluateTaskApply({
        draft: normalizeApplyDraft(this.draft)
      })
      this.applyServerSnapshot(result)
    },
    async finishCardConfirm(kind, snapshotText) {
      const seq = this.sessionSeq
      this.evaluateLoading = true
      try {
        await this.refreshFromEvaluate()
        if (seq !== this.sessionSeq) return
      } catch (error) {
        if (seq !== this.sessionSeq) return
        const errorText = (error && error.message) || '状态评估失败，请稍后重试'
        this.pushMessage('assistant', errorText)
        this.scrollBodyToLatest()
        this.$message.error(errorText)
        return
      } finally {
        if (seq === this.sessionSeq) {
          this.evaluateLoading = false
        }
      }
      this.dismissActiveAction()
      this.pushReceipt(kind, snapshotText)
      // 渠道/关键词/模式/周期确认成功后作废预估；任务名确认不清空
      if (RULE_CONFIRM_KINDS.indexOf(kind) >= 0) {
        this.clearEstimateDecisionAndResult()
      }
      // 风险卡插在 nextAction 流程卡之前，且不与 estimate/review 叠
      if (this.shouldInsertRiskHint(kind)) {
        this.appendRiskHint(kind === NEXT_ACTION_CHANNELS ? RISK_SCOPE_CHANNELS : RISK_SCOPE_KEYWORDS)
        this.scrollBodyToLatest()
        return
      }
      this.ensureActionCards()
      this.scrollBodyToLatest()
    },
    findEstimateAction() {
      return this.conversationItems.find(item => item.type === 'action' && item.kind === ACTION_KIND_ESTIMATE) || null
    },
    removeEstimateActions() {
      const hadEstimate = !!this.findEstimateAction()
      this.conversationItems = this.conversationItems.filter(
        item => !(item.type === 'action' && item.kind === ACTION_KIND_ESTIMATE)
      )
      if (hadEstimate) {
        this.invalidateEstimateRequest()
      }
    },
    pendingEstimateGate() {
      return !this.estimateAccepted && !this.estimateSkipped
    },
    // 只认 Java nextAction，不解析 Agent 文本，不用 missingFields 拼步骤
    ensureActionCards() {
      if (this.nextAction !== NEXT_ACTION_REVIEW) {
        this.removeReviewActions()
        this.removeEstimateActions()
      }
      const riskActive = this.findActiveAction()
      if (riskActive && riskActive.kind === ACTION_KIND_RISK_HINT) {
        return
      }
      const ruleChanged = this.invalidateEstimateIfRuleChanged()
      const active = this.findActiveAction()
      if (active) {
        if (this.nextAction === NEXT_ACTION_REVIEW && this.pendingEstimateGate()) {
          if (active.kind === ACTION_KIND_ESTIMATE) {
            if (ruleChanged) {
              this.startEstimate()
            }
            return
          }
          // 规则已作废时，完结卡要让位给量级卡；回执「修改」仍只保留一张
          if (active.fromReceiptEdit) return
          this.dismissActiveAction()
        } else {
          if (active.kind === this.nextAction) return
          // 回执「修改」打开的卡允许与 nextAction 不一致，等用户确认，禁止再叠一张
          if (active.fromReceiptEdit) return
          // Agent 已推进 nextAction，撤掉过期流程卡后再出对应卡
          this.dismissActiveAction()
        }
      }
      if (this.nextAction === NEXT_ACTION_REVIEW) {
        if (this.pendingEstimateGate()) {
          this.removeReviewActions()
          if (!this.findEstimateAction()) {
            this.appendAction(ACTION_KIND_ESTIMATE)
            this.startEstimate()
          } else if (ruleChanged) {
            this.startEstimate()
          }
          return
        }
        this.removeEstimateActions()
        if (!this.findReviewAction()) {
          this.appendAction(NEXT_ACTION_REVIEW)
        }
        return
      }
      if (this.nextAction === NEXT_ACTION_CHANNELS) {
        this.prefillChannelActionUi()
        this.appendAction(NEXT_ACTION_CHANNELS)
        return
      }
      if (this.nextAction === NEXT_ACTION_KEYWORDS) {
        this.prefillKeywordActionUi()
        this.appendAction(NEXT_ACTION_KEYWORDS)
        return
      }
      if (this.nextAction === NEXT_ACTION_COLLECT_MODE) {
        this.appendAction(NEXT_ACTION_COLLECT_MODE)
        return
      }
      if (this.nextAction === NEXT_ACTION_COLLECT_PERIOD) {
        this.prefillPeriodActionUi()
        this.appendAction(NEXT_ACTION_COLLECT_PERIOD)
        return
      }
      if (this.nextAction === NEXT_ACTION_TASK_NAME) {
        this.prefillTaskNameActionUi()
        this.appendAction(NEXT_ACTION_TASK_NAME)
      }
    },
    scrollBodyToLatest() {
      this.$nextTick(() => {
        const body = this.$refs.assistBody
        if (!body) return
        const nodes = body.querySelectorAll('.apply-assist-item')
        const last = nodes[nodes.length - 1]
        if (!last) {
          body.scrollTop = body.scrollHeight
          return
        }
        const delta = last.getBoundingClientRect().top - body.getBoundingClientRect().top
        body.scrollTop += delta - 8
      })
    },
    onChannelsChange(paths) {
      this.actionUi.channelPaths = Array.isArray(paths) ? paths : []
    },
    async confirmChannels() {
      if (this.busy) return
      const channels = channelsFromPaths(this.actionUi.channelPaths)
      if (!channels.length) {
        this.$message.warning('请选择有效渠道')
        return
      }
      this.draft = normalizeApplyDraft({
        ...this.draft,
        channels: channels
      })
      await this.finishCardConfirm(NEXT_ACTION_CHANNELS, formatDraftChannels(this.draft) || '已确认渠道')
    },
    onSelectScheme(index) {
      this.actionUi.keywords.selected = index
    },
    async confirmKeywordScheme() {
      if (this.busy) return
      const scheme = this.actionUi.keywords.schemes[this.actionUi.keywords.selected]
      if (!scheme) {
        this.$message.warning('请先选择一个关键词方案')
        return
      }
      const groups = (Array.isArray(scheme.keywordGroups) ? scheme.keywordGroups : [])
        .map(words => ({
          words: (words || []).map(word => String(word).trim()).filter(Boolean)
        }))
        .filter(group => group.words.length > 0)
      if (!groups.length) {
        this.$message.warning('所选方案没有可用关键词')
        return
      }
      this.draft = normalizeApplyDraft({
        ...this.draft,
        monitorObject: (this.actionUi.keywords.object || '').trim() || this.draft.monitorObject,
        monitorHint: (this.actionUi.keywords.hint || '').trim(),
        keywordGroups: groups,
        excludeWords: (Array.isArray(scheme.excludeWords) ? scheme.excludeWords : [])
          .map(word => String(word).trim())
          .filter(Boolean),
        keywordsConfirmed: true
      })
      await this.finishCardConfirm(NEXT_ACTION_KEYWORDS, formatDraftKeywords(this.draft) || '已确认关键词方案')
    },
    async confirmCollectMode(mode) {
      if (this.busy) return
      if (mode !== COLLECT_MODE_HISTORY && mode !== COLLECT_MODE_REALTIME) return
      const currentRange = buildDateTimeRange(this.draft.startTime, this.draft.endTime)
      const periodInvalid = !!validateCollectDateRange(mode, currentRange)
      this.draft = normalizeApplyDraft({
        ...this.draft,
        collectMode: mode,
        startTime: periodInvalid ? '' : this.draft.startTime,
        endTime: periodInvalid ? '' : this.draft.endTime
      })
      await this.finishCardConfirm(NEXT_ACTION_COLLECT_MODE, this.draft.collectMode || '已确认采集模式')
    },
    async confirmCollectPeriod() {
      if (this.busy) return
      const rangeError = validateCollectDateRange(this.draft.collectMode, this.actionUi.periodRange)
      if (rangeError) {
        this.$message.warning(rangeError)
        return
      }
      this.draft = normalizeApplyDraft({
        ...this.draft,
        startTime: toApiDateTime(this.actionUi.periodRange[0]),
        endTime: toApiDateTime(this.actionUi.periodRange[1])
      })
      await this.finishCardConfirm(NEXT_ACTION_COLLECT_PERIOD, formatDraftPeriod(this.draft) || '已确认采集周期')
    },
    async confirmTaskName() {
      if (this.busy) return
      const taskName = (this.actionUi.taskName || '').trim()
      if (!taskName) {
        this.$message.warning('请输入任务名称')
        return
      }
      this.draft = normalizeApplyDraft({
        ...this.draft,
        taskName: taskName
      })
      await this.finishCardConfirm(NEXT_ACTION_TASK_NAME, this.draft.taskName || '已确认任务名称')
    },
    dismissReviewCard() {
      this.removeReviewActions()
    },
    async startEstimate() {
      const action = this.findEstimateAction()
      if (!action) return
      this.estimateSeq += 1
      const reqSeq = this.estimateSeq
      const session = this.sessionSeq
      this.estimateLoading = true
      this.estimatedCount = null
      this.estimatedAt = null
      this.estimateError = ''
      try {
        const result = await estimateApply(buildEstimatePayloadFromDraft(this.draft))
        if (reqSeq !== this.estimateSeq || session !== this.sessionSeq) return
        if (!this.findEstimateAction()) return
        const count = result && result.estimatedCount != null ? result.estimatedCount : null
        const error = result && result.estimateError ? String(result.estimateError).trim() : ''
        this.estimatedAt = result && result.estimatedAt ? result.estimatedAt : null
        if (error || count == null) {
          this.estimatedCount = null
          this.estimateError = error || '预估失败'
          return
        }
        this.estimatedCount = count
        this.estimateError = ''
      } catch (error) {
        if (reqSeq !== this.estimateSeq || session !== this.sessionSeq) return
        if (!this.findEstimateAction()) return
        this.estimatedCount = null
        this.estimatedAt = null
        this.estimateError = (error && error.message) || '预估失败'
      } finally {
        if (reqSeq === this.estimateSeq && session === this.sessionSeq) {
          this.estimateLoading = false
        }
      }
    },
    retryEstimate() {
      this.startEstimate()
    },
    acceptEstimate() {
      this.estimateAccepted = true
      this.estimateSkipped = false
      this.removeEstimateActions()
      this.ensureActionCards()
      this.scrollBodyToLatest()
    },
    skipEstimate() {
      this.estimateSkipped = true
      this.estimateAccepted = false
      this.removeEstimateActions()
      this.ensureActionCards()
      this.scrollBodyToLatest()
    },
    rejectEstimateTooLarge() {
      this.estimateAccepted = false
      this.removeEstimateActions()
      this.scrollBodyToLatest()
    },
    editReceipt(item) {
      if (this.assistLoading || this.evaluateLoading || !item) return
      this.dismissActiveAction()
      this.invalidateEstimateRequest()
      if (item.kind === NEXT_ACTION_CHANNELS) {
        this.prefillChannelActionUi()
        this.appendAction(NEXT_ACTION_CHANNELS, true)
      } else if (item.kind === NEXT_ACTION_KEYWORDS) {
        this.prefillKeywordActionUi()
        this.appendAction(NEXT_ACTION_KEYWORDS, true)
      } else if (item.kind === NEXT_ACTION_COLLECT_MODE) {
        this.appendAction(NEXT_ACTION_COLLECT_MODE, true)
      } else if (item.kind === NEXT_ACTION_COLLECT_PERIOD) {
        this.prefillPeriodActionUi()
        this.appendAction(NEXT_ACTION_COLLECT_PERIOD, true)
      } else if (item.kind === NEXT_ACTION_TASK_NAME) {
        this.prefillTaskNameActionUi()
        this.appendAction(NEXT_ACTION_TASK_NAME, true)
      }
      this.scrollBodyToLatest()
    },
    async sendMessage() {
      const text = (this.inputText || '').trim()
      if (!text || this.busy) return
      this.inputText = ''
      this.pushMessage('user', text)
      this.scrollBodyToLatest()
      const seq = this.sessionSeq
      this.assistLoading = true
      try {
        const result = await assistTaskApply({
          message: text,
          draft: normalizeApplyDraft(this.draft)
        })
        if (seq !== this.sessionSeq) return
        this.applyServerSnapshot(result)
        this.pushMessage('assistant', (result && result.reply) || '本轮没有返回说明，请再试一次。')
        this.ensureActionCards()
        this.scrollBodyToLatest()
      } catch (error) {
        if (seq !== this.sessionSeq) return
        const errorText = (error && error.message) || '申请辅助失败，请稍后重试'
        this.pushMessage('assistant', errorText)
        this.scrollBodyToLatest()
        this.$message.error(errorText)
      } finally {
        if (seq === this.sessionSeq) {
          this.assistLoading = false
        }
      }
    },
    async fetchKeywordRecommend() {
      const objectName = (this.actionUi.keywords.object || '').trim()
      if (!objectName) {
        this.actionUi.keywords.error = '请输入监控对象'
        return
      }
      if (this.busy) return
      const active = this.findActiveAction()
      if (!active || active.kind !== 'keywords') return
      const actionId = active.id
      const seq = this.sessionSeq
      this.actionUi.keywords.loading = true
      this.actionUi.keywords.requestActionId = actionId
      this.actionUi.keywords.error = ''
      try {
        const result = await recommendKeywordSchemes({
          object: objectName,
          hint: (this.actionUi.keywords.hint || '').trim()
        })
        if (seq !== this.sessionSeq) return
        if (!this.isKeywordActionCurrent(actionId)) return
        const schemes = result && Array.isArray(result.schemes) ? result.schemes : []
        this.actionUi.keywords.schemes = schemes
        this.actionUi.keywords.selected = -1
        if (!schemes.length) {
          this.actionUi.keywords.error = '没有可用方案，请调整对象或说明后重试'
        }
      } catch (error) {
        if (seq !== this.sessionSeq) return
        if (!this.isKeywordActionCurrent(actionId)) return
        this.actionUi.keywords.schemes = []
        this.actionUi.keywords.selected = -1
        this.actionUi.keywords.error = (error && error.message) || '智能推荐失败，请稍后重试'
      } finally {
        if (seq === this.sessionSeq) {
          this.actionUi.keywords.loading = false
        }
      }
    },
    adoptDraft() {
      if (!this.canGenerateDraft || this.busy) return
      this.$emit('adopt', normalizeApplyDraft(this.draft))
      this.drawerVisible = false
    }
  }
}
</script>

<style>
.apply-assist-drawer .el-drawer__header {
  margin-bottom: 0;
  padding: 18px 24px;
  border-bottom: 1px solid #eef2f7;
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
}

.apply-assist-drawer .el-drawer__body {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
  overflow: hidden;
}

.apply-assist-summary {
  flex-shrink: 0;
  margin: 12px 20px 0;
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

.apply-assist-summary-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
  cursor: pointer;
  user-select: none;
}

.apply-assist-summary-body {
  margin-top: 8px;
}

.apply-assist-summary-row {
  display: flex;
  gap: 8px;
  font-size: 12px;
  line-height: 1.7;
}

.apply-assist-summary-row span {
  width: 64px;
  color: #94a3b8;
  flex-shrink: 0;
}

.apply-assist-summary-row b {
  font-weight: 500;
  color: #1f2d3d;
}

.apply-assist-missing {
  margin-top: 8px;
  font-size: 12px;
  color: #e6a23c;
}

.apply-assist-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.apply-assist-item {
  display: flex;
  flex-direction: column;
}

.apply-assist-item.is-message {
  align-items: stretch;
}

.apply-assist-message {
  max-width: 92%;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}

.apply-assist-message.is-user {
  align-self: flex-end;
  background: #2f5bff;
  color: #fff;
}

.apply-assist-message.is-assistant {
  align-self: flex-start;
  background: #f5f7fb;
  color: #1f2d3d;
}

.apply-assist-card,
.apply-assist-receipt {
  border: 1px solid #eef2f7;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

.apply-assist-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 8px;
}

.apply-assist-estimate-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}

.apply-assist-estimate-text {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2d3d;
}

.apply-assist-estimate-disclaimer {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}

.apply-assist-risk-fact {
  font-size: 13px;
  line-height: 1.6;
  color: #1f2d3d;
}

.apply-assist-risk-fact + .apply-assist-risk-fact {
  margin-top: 6px;
}

.apply-assist-card-error {
  margin-top: 8px;
  font-size: 12px;
  color: #e6a23c;
  display: flex;
  align-items: center;
  gap: 4px;
}

.apply-assist-card-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.apply-assist-mode-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.apply-assist-mode-actions .el-button {
  margin-left: 0;
}

.apply-assist-receipt-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #1f2d3d;
}

.apply-assist-receipt-body {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: #1f2d3d;
}

.apply-assist-composer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #eef2f7;
  background: #fff;
}

.apply-assist-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid #eef2f7;
  background: #fff;
}
</style>
